import { useEffect, useMemo, useRef, useState } from 'react';
import type { EvidenceLabel, VideoAnalysis } from '@sr-test-wizard/shared';
import { useVideosStore } from '../../state/videosStore';
import { runVideosApi } from '../../lib/runVideosApi';
import { FramesZoomModal, type FramesZoomItem } from '../../components/FramesZoomModal';
import { evidenceLabelsApi } from '../../lib/evidenceLabelsApi';

interface Props {
  runId: string;
  slug: string;
  analysis: VideoAnalysis;
  /** Optional title rendered inline on the same row as the filter input. */
  title?: string;
}

function formatClock(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/**
 * Scrollable list of `[hh:mm:ss] [open frame] text` rows. The row matching
 * the player's `currentSecond` gets a highlighted style and is auto-scrolled
 * into view. Clicking a frame opens an in-app zoom modal with prev/next
 * navigation across the currently filtered list.
 *
 * The list is not strictly "virtualized" (the row count is bounded by a
 * 15-min @ 1 fps clip = 900 entries, well within React's comfort zone) but
 * the implementation keeps DOM updates cheap by using a stable `ref` map
 * for the auto-scroll target instead of re-rendering everything on every
 * second tick.
 */
const COLLAPSED_KEY = 'sneaky:videos:analysisCollapsed';
const REAL_FRAME_RE = /^frame_\d+\.jpe?g$/i;

export function VideoAnalysisList({ runId, slug, analysis, title }: Props) {
  const currentSecond = useVideosStore((s) => s.currentSecond);
  const [filter, setFilter] = useState('');
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  });
  // Multi-select label filter, in addition to the text filter. OR within
  // the selection (a frame matches when its labels include ANY of the
  // selected keys); AND across the selection and the text filter.
  const [selectedLabels, setSelectedLabels] = useState<Set<string>>(() => new Set());
  // Loaded once per mount: definition lookup for chip tooltips and for
  // populating the filter dropdown with the canonical set of visual
  // labels (rather than only labels currently used in this clip).
  const [labelDefs, setLabelDefs] = useState<EvidenceLabel[]>([]);
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    let cancelled = false;
    void evidenceLabelsApi
      .list()
      .then((bundle) => {
        if (!cancelled) setLabelDefs(bundle.entries);
      })
      .catch(() => {
        // The chips degrade gracefully without definitions; we just lose
        // the tooltip text and fall back to whatever labels happen to be
        // present on the frame rows.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const labelDefByKey = useMemo(() => {
    const map = new Map<string, EvidenceLabel>();
    for (const def of labelDefs) {
      map.set(def.key.toLowerCase(), def);
    }
    return map;
  }, [labelDefs]);

  // Distinct labels seen in this clip's frames - used to populate the
  // chip filter so we only offer values the user can actually narrow by.
  // Falls back on the canonical visual taxonomy when a clip is empty.
  const availableLabels = useMemo(() => {
    const seen = new Set<string>();
    for (const f of analysis.frames) {
      for (const l of f.labels ?? []) seen.add(l);
    }
    return Array.from(seen).sort();
  }, [analysis.frames]);

  const toggleLabel = (key: string) => {
    setSelectedLabels((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const labelSel = selectedLabels;
    return analysis.frames.filter((f) => {
      if (labelSel.size > 0) {
        const frameLabels = f.labels ?? [];
        const hit = frameLabels.some((l) => labelSel.has(l));
        if (!hit) return false;
      }
      if (!q) return true;
      return (
        f.text.toLowerCase().includes(q) ||
        formatClock(f.second).includes(q) ||
        (f.labels ?? []).some((l) => l.toLowerCase().includes(q))
      );
    });
  }, [analysis.frames, filter, selectedLabels]);

  // Modal sequence mirrors the visible filtered list so prev/next walks the
  // exact rows the user sees. If the filter changes we drop the open modal
  // because the indices no longer match (reopening is one click).
  const zoomableRows = useMemo(
    () => filtered.filter((f) => REAL_FRAME_RE.test(f.frame)),
    [filtered],
  );
  const zoomItems = useMemo<FramesZoomItem[]>(
    () =>
      zoomableRows.map((f) => ({
        url: runVideosApi.frameUrl(runId, slug, f.frame),
        caption: `${slug} @ ${formatClock(f.second)}`,
      })),
    [zoomableRows, runId, slug],
  );

  useEffect(() => {
    setZoomIndex(null);
  }, [filter]);

  // Auto-scroll so the active row is pinned to the top of the visible
  // viewport. We scroll the inner container only (never the outer page),
  // and use bounding-rect deltas so the math is correct regardless of the
  // container's positioning context. (target.offsetTop would be relative
  // to the nearest *positioned* ancestor, not the scroll container.)
  useEffect(() => {
    const container = containerRef.current;
    const target = rowRefs.current.get(currentSecond);
    if (!container || !target) return;
    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    const delta = tRect.top - cRect.top;
    if (Math.abs(delta) < 1) return;
    container.scrollTo({
      top: container.scrollTop + delta,
      behavior: 'smooth',
    });
  }, [currentSecond]);

  if (analysis.frames.length === 0) {
    return (
      <p className="muted small" style={{ padding: 12 }}>
        No frames analysed yet for <code>{slug}</code>. Run extract + analyse.
      </p>
    );
  }

  // When collapsed, show only the row matching the player's current second
  // (so the user always sees the analysis text for the frame currently on
  // screen). When expanded, show the filter and all rows as today.
  const visibleRows = collapsed
    ? filtered.filter((f) => f.second === currentSecond)
    : filtered;

  return (
    <div className="video-analysis-list">
      <div
        className="row"
        style={{ padding: '0 2px 6px', gap: 8, alignItems: 'center' }}
      >
        <button
          type="button"
          className="btn btn-ghost small"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand list (show all frames)' : 'Collapse list (current frame only)'}
          style={{ padding: '0 6px', flexShrink: 0 }}
        >
          {collapsed ? '\u25B6' : '\u25BC'}
        </button>
        {!collapsed && title && (
          <strong className="small" style={{ flexShrink: 0 }}>
            {title}
          </strong>
        )}
        {!collapsed && (
          <>
            <input
              type="search"
              className="input small"
              placeholder="Filter (timestamp, text or label)"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ flex: 1, minWidth: 0 }}
            />
            <span className="muted small" style={{ flexShrink: 0 }}>
              {filtered.length}/{analysis.frames.length}
            </span>
          </>
        )}
        {collapsed && (
          <span className="muted small" style={{ flex: 1, minWidth: 0 }}>
            Current frame only
          </span>
        )}
      </div>
      {!collapsed && availableLabels.length > 0 && (
        <div
          className="row"
          style={{
            padding: '0 2px 6px',
            gap: 6,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span className="muted small" style={{ flexShrink: 0 }}>
            Labels:
          </span>
          {availableLabels.map((key) => {
            const def = labelDefByKey.get(key.toLowerCase());
            const active = selectedLabels.has(key);
            return (
              <button
                key={key}
                type="button"
                className={`badge small${active ? ' good' : ''}`}
                onClick={() => toggleLabel(key)}
                title={def?.definition ?? key}
                style={{
                  cursor: 'pointer',
                  border: active
                    ? '1px solid var(--good)'
                    : '1px solid var(--border)',
                  background: active ? 'rgba(34, 197, 94, 0.15)' : undefined,
                  fontFamily: 'monospace',
                }}
              >
                {key}
              </button>
            );
          })}
          {selectedLabels.size > 0 && (
            <button
              type="button"
              className="btn btn-ghost small"
              onClick={() => setSelectedLabels(new Set())}
              title="Clear label filter"
              style={{ padding: '0 8px' }}
            >
              Clear
            </button>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          maxHeight: collapsed ? 'none' : 420,
          overflowY: collapsed ? 'visible' : 'auto',
          border: '1px solid var(--border)',
          borderRadius: 6,
        }}
      >
        {visibleRows.length === 0 && collapsed && (
          <p
            className="muted small"
            style={{ padding: '10px 12px', margin: 0 }}
          >
            No analysis for second {formatClock(currentSecond)} - press play.
          </p>
        )}
        {visibleRows.map((frame) => {
          const active = frame.second === currentSecond;
          const zoomableIdx = zoomableRows.findIndex((f) => f.second === frame.second);
          const canZoom = zoomableIdx >= 0;
          return (
            <div
              key={frame.second}
              ref={(el) => {
                if (el) rowRefs.current.set(frame.second, el);
                else rowRefs.current.delete(frame.second);
              }}
              className={`video-analysis-row${active ? ' is-active' : ''}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '92px 70px 1fr',
                gap: 8,
                padding: '8px 10px',
                borderBottom: '1px solid var(--border)',
                background: active ? 'rgba(210, 153, 34, 0.18)' : 'transparent',
                boxShadow: active ? 'inset 3px 0 0 var(--warn)' : 'none',
                fontSize: 13,
                lineHeight: 1.45,
                fontWeight: active ? 600 : 400,
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <code className="mono small">{formatClock(frame.second)}</code>
                {active && (
                  <span
                    style={{
                      background: 'var(--accent)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      padding: '1px 8px',
                      lineHeight: '14px',
                      borderRadius: 999,
                    }}
                  >
                    Now
                  </span>
                )}
              </span>
              <button
                type="button"
                className="btn btn-ghost small"
                onClick={() => {
                  if (canZoom) setZoomIndex(zoomableIdx);
                }}
                title={frame.frame}
                style={{ padding: '0 6px', justifySelf: 'start' }}
                disabled={!canZoom}
              >
                {canZoom ? 'Frame' : 'Native'}
              </button>
              <div
                style={{
                  color: frame.error ? 'var(--bad)' : 'inherit',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {(frame.labels ?? []).length > 0 && (
                  <div
                    className="row"
                    style={{
                      gap: 4,
                      flexWrap: 'wrap',
                      marginBottom: 4,
                      alignItems: 'center',
                    }}
                  >
                    {(frame.labels ?? []).map((l) => {
                      const def = labelDefByKey.get(l.toLowerCase());
                      return (
                        <span
                          key={l}
                          className="badge small"
                          title={def?.definition ?? l}
                          style={{
                            fontFamily: 'monospace',
                            background: 'rgba(99, 102, 241, 0.12)',
                            border: '1px solid rgba(99, 102, 241, 0.4)',
                            color: 'var(--fg)',
                          }}
                        >
                          {l}
                        </span>
                      );
                    })}
                    {frame.labelConfidence && (
                      <span
                        className="muted small"
                        title="Tagger's self-reported confidence in this label set"
                        style={{ fontSize: 10 }}
                      >
                        ({frame.labelConfidence})
                      </span>
                    )}
                  </div>
                )}
                {frame.error ? `(error) ${frame.text}` : frame.text}
              </div>
            </div>
          );
        })}
      </div>
      {zoomIndex !== null && zoomItems.length > 0 && (
        <FramesZoomModal
          items={zoomItems}
          index={Math.min(zoomIndex, zoomItems.length - 1)}
          onChange={setZoomIndex}
          onClose={() => setZoomIndex(null)}
        />
      )}
    </div>
  );
}
